import React, { useEffect, useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { API_BASE_URL } from "../../../config";
import { Link } from "react-router-dom";
import Pagination from "../../Pagination/Paginations.js";
import EntriesSelector from "../../Pagination/EntriesSelector";
import useSortData from "../../Pagination/SortData";

const PermissionReport = () => {

 const [filters, setFilters] = useState({
  start_date: new Date(),
  end_date: new Date(),
  dept_id: "0"
});

  const [departments, setDepartments] = useState([]);
  const [data, setData] = useState([]);
  const [totalHours, setTotalHours] = useState("00:00");

  // 🔹 Datatable state (search / pagination / sorting) — same as My Shift
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const { sortConfig, sortData, handleSort } = useSortData();

  const clearInput = () => setSearchTerm("");

  // ✅ Load departments
  useEffect(() => {

    const token = localStorage.getItem("authToken");

    if (!token) {
      window.location.href = "/login";
      return;
    }

    axios.get(`${API_BASE_URL}/permission_report`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    .then(res => {
      console.log("Departments API:", res.data);
      setDepartments(res.data?.data?.departments || []);
    })
    .catch(err => {
      console.error(err);
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        window.location.href = "/login";
      }
      setDepartments([]);
    });

  }, []);

  // 🔹 Handle change
  const handleChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  // 🔹 Fetch report
  const fetchReport = () => {

    if (!filters.start_date || !filters.end_date) {
      alert("Please select date range");
      return;
    }

    const toLocalDate = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    const payload = {
      dept_id: filters.dept_id,
      start_date: toLocalDate(filters.start_date),
      end_date: toLocalDate(filters.end_date),
    };

    console.log("Payload:", payload);

    axios.get(`${API_BASE_URL}/permission_report_list`, {
      params: payload,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`
      }
    })
    .then(res => {
      console.log("Report Data:", res.data);
      const tableData = res.data?.data || [];
      setData(tableData);
      setCurrentPage(1);
      setTotalHours(res.data?.total_hours || "00:00");
    })
    .catch(err => {
      console.error("Report API Error:", err);
    });
  };

  const columns = [
    { label: "Employee ID", key: "employee_id", sortable: true },
    { label: "Employee Name", key: "employee_name", sortable: true },
    { label: "Department", key: "department", sortable: true },
    { label: "Hours", key: "hours", sortable: true },
    { label: "Date", key: "date", sortable: true },
    { label: "Reason", key: "reason", sortable: false }
  ];

  // 🔹 Search across the visible columns, then sort + paginate
  const filteredItems = data.filter((item) =>
    `${item.employee_id ?? ""}${item.employee_name ?? ""}${item.department ?? ""}${item.hours ?? ""}${item.date ?? ""}${item.reason ?? ""}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredItems.length / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const sortedItems = sortData(filteredItems);
  const filteredData = sortedItems.slice(indexOfFirst, indexOfLast);



  const downloadCSV = () => {
  if (!data.length) {
    alert("No data to download");
    return;
  }

  const headers = [
    "Employee ID",
    "Employee Name",
    "Department",
    "Hours",
    "Date",
    "Reason"
  ];

  const rows = data.map(row => [
    row.employee_id,
    row.employee_name,
    row.department,
    row.hours,
    row.date,
    row.reason
  ]);

  let csvContent =
    "data:text/csv;charset=utf-8," +
    [headers, ...rows]
      .map(e => e.map(v => `"${v ?? ""}"`).join(","))
      .join("\n");

  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");

  link.setAttribute("href", encodedUri);
  link.setAttribute("download", "permission_report.csv");
  document.body.appendChild(link);

  link.click();
  document.body.removeChild(link);
};
  return (
    <div className="container-fluid">

      {/* Navigation */}
      <div className="mb-3 d-flex" style={{ gap: "10px" }}>
        <Link to="/permission" className="btn btn-outline-secondary btn-sm">
          Permission
        </Link>
        <Link to="/permission-report" className="btn btn-primary btn-sm">
          Permission Report
        </Link>
      </div>

      {/* ✅ All filters in one perfectly aligned row */}
      <div className="row g-3 mb-3" style={{ alignItems: "flex-end" }}>

        <div className="col-md-3 d-flex flex-column">
          <label className="form-label mb-1" style={{ height: "24px" }}>From Date</label>
          <DatePicker
            selected={filters.start_date}
            onChange={(date) => setFilters({ ...filters, start_date: date })}
            className="form-control"
            dateFormat="yyyy-MM-dd"
            placeholderText="Select start date"
            wrapperClassName="w-100"
          />
        </div>

        <div className="col-md-3 d-flex flex-column">
          <label className="form-label mb-1" style={{ height: "24px" }}>To Date</label>
          <DatePicker
            selected={filters.end_date}
            onChange={(date) => setFilters({ ...filters, end_date: date })}
            className="form-control"
            dateFormat="yyyy-MM-dd"
            placeholderText="Select end date"
            wrapperClassName="w-100"
          />
        </div>

        <div className="col-md-3 d-flex flex-column">
          <label className="form-label mb-1" style={{ height: "24px" }}>Department</label>
          <select
            name="dept_id"
            onChange={handleChange}
            className="form-control"
            value={filters.dept_id}
            style={{ height: "38px" }}
          >
            <option value="0">All</option>
            {departments.length > 0 ? (
              departments.map(d => (
                <option key={d.department_id} value={d.department_id}>
                  {d.department_name}
                </option>
              ))
            ) : (
              <option disabled>No departments found</option>
            )}
          </select>
        </div>

        {/* Get + CSV — compact buttons aligned with the fields */}
        <div className="col-md-3 d-flex align-items-end">
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            onClick={fetchReport}
            style={{ height: "38px" }}
          >
            Get
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={downloadCSV}
            style={{ height: "38px", marginLeft: "15px" }}
          >
            CSV
          </button>
        </div>

      </div>

      {/* Report table — heading sits inside the card like the Permission page */}
      <div className="card mt-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h3 className="card-title d-flex align-items-center mb-0" style={{ color: "#1d3a8a", fontWeight: 600 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="#1d3a8a" style={{ marginRight: "8px", verticalAlign: "-3px" }}>
              <path fillRule="evenodd" d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m1.679-4.493-1.335 2.226a.75.75 0 0 1-1.174.144l-.774-.773a.5.5 0 0 1 .708-.708l.547.548 1.017-1.694a.5.5 0 1 1 .858.257z"/>
              <path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0M8 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4"/>
              <path d="M8.256 14a4.5 4.5 0 0 1-.229-1.004H3c.001-.246.154-.986.832-1.664C4.484 10.68 5.711 10 8 10q.39 0 .74.025c.226-.341.496-.65.804-.918Q8.844 9.002 8 9c-5 0-6 3-6 4s1 1 1 1z"/>
            </svg>
            Permission Report
          </h3>
          <h5 className="mb-0">Total Permission Hours = {totalHours}</h5>
        </div>

        {/* Entries selector + search box */}
        <div className="card-header">
          <div className="card-options" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <EntriesSelector
              entriesPerPage={entriesPerPage}
              setEntriesPerPage={setEntriesPerPage}
              setCurrentPage={setCurrentPage}
            />

            <div style={{ position: "relative", width: "200px" }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ width: "100%", height: "30px", fontSize: "13px", borderRadius: "4px", paddingRight: "28px" }}
              />
              {searchTerm.length > 0 && (
                <span
                  onClick={clearInput}
                  style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#aaa", fontSize: "16px", userSelect: "none" }}
                >
                  ✖
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="table-responsive sticky-table-wrap" style={{ maxHeight: "420px", overflowY: "auto" }}>
            <table className="table table-hover table-striped table-vcenter text-nowrap mb-0 sticky-table sticky-no-freeze-col">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      onClick={col.sortable ? () => handleSort(col.key) : null}
                      style={{ cursor: col.sortable ? "pointer" : "default" }}
                    >
                      {col.label}
                      {col.sortable && (
                        sortConfig.key === col.key
                          ? (sortConfig.direction === "asc" ? " ▲" : " ▼")
                          : " ⇅"
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.employee_id}</td>
                      <td>{row.employee_name}</td>
                      <td>{row.department}</td>
                      <td>{row.hours}</td>
                      <td>{row.date}</td>
                      <td>{row.reason}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ color: "red", textAlign: "center", fontWeight: "bold" }}>
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

    </div>
  );
};

export default PermissionReport;